import React, { useEffect, useRef } from "react";
import { FaTimes } from "react-icons/fa";

export default function FeatureDetailsModal({ feature, onClose }) {
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!feature) return;

    // Focus trap and keyboard handlers
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    // Focus the close button when modal opens
    if (closeButtonRef.current) {
      closeButtonRef.current.focus();
    }

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [feature, onClose]);

  const handleBackdropClick = (e) => {
    if (e.target === modalRef.current) {
      onClose();
    }
  };

  if (!feature) return null;

  const Icon = feature.icon;

  return (
    <div
      ref={modalRef}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="feature-modal-title"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto relative my-8">
        {/* Close Button */}
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="absolute top-4 right-4 text-snaptrix-gray-500 hover:text-snaptrix-gray-700 text-2xl p-2 rounded-full hover:bg-snaptrix-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-snaptrix-blue-600 z-10"
          aria-label="Close modal"
        >
          <FaTimes aria-hidden="true" />
        </button>

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-snaptrix-gray-200 p-6 md:p-8 rounded-t-2xl z-10">
          <div className="flex items-center gap-4 mb-2">
            <div className="p-3 bg-snaptrix-blue-50 rounded-lg">
              <Icon className="text-snaptrix-blue-600 text-3xl" aria-hidden="true" />
            </div>
            <h2
              id="feature-modal-title"
              className="text-2xl md:text-3xl font-bold text-snaptrix-gray-700"
            >
              {feature.name}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 space-y-6">
          {/* Description */}
          <section>
            <h3 className="text-lg font-semibold text-snaptrix-gray-700 mb-3">
              What it does
            </h3>
            <p className="text-snaptrix-gray-600 leading-relaxed">{feature.fullDescription}</p>
          </section>

          {/* Workflow */}
          <section>
            <h3 className="text-lg font-semibold text-snaptrix-gray-700 mb-4">
              Typical Workflow
            </h3>
            <ol className="space-y-3">
              {feature.workflow.map((step, index) => (
                <li key={index} className="flex gap-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-snaptrix-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {index + 1}
                  </span>
                  <span className="text-snaptrix-gray-600 leading-relaxed pt-1">{step}</span>
                </li>
              ))}
            </ol>
          </section>

          {/* Use Case */}
          <section className="bg-snaptrix-blue-50 rounded-lg p-5 border-l-4 border-snaptrix-blue-600">
            <h3 className="text-lg font-semibold text-snaptrix-gray-700 mb-2">
              Best For
            </h3>
            <p className="text-snaptrix-gray-600 leading-relaxed">{feature.useCase}</p>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-snaptrix-gray-50 border-t border-snaptrix-gray-200 p-6 rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full bg-snaptrix-blue-600 hover:bg-snaptrix-blue-700 text-white py-3 px-6 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-4 focus:ring-snaptrix-blue-200"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}
